/** Sections of the lease generation form a preview click can land on. */
export type LeaseFormSection = 'general' | 'lessee' | 'owner' | 'tracts' | 'terms' | 'addendum' | 'acknowledgment';

export interface ILeasePreviewTarget {
    section: LeaseFormSection;
    /** Form field name (or data-preview-field) to focus; omitted when the value isn't edited on this form. */
    field?: string;
    /** Where the value comes from, for the hover text when there's no field to jump to. */
    source?: string;
}

const field = (section: LeaseFormSection, name: string): ILeasePreviewTarget => ({ section, field: name });
const from = (section: LeaseFormSection, source: string): ILeasePreviewTarget => ({ section, source });

const OWNER_RECORD = 'the owner record';
const TRACT_LIST = 'the included tracts';
const PROJECT_SETTINGS = 'project settings';

/**
 * Where each merge token (LeaseTemplateTokens on the server) is edited on the oil and gas lease form.
 * Keys are lower-case; tokens match case-insensitively, as they do in the merge.
 */
const TARGETS: { [token: string]: ILeasePreviewTarget } = {
    leasedate: field('terms', 'leaseDate'),
    leasedateformatted: field('terms', 'leaseDate'),
    leasedateallcaps: field('terms', 'leaseDate'),
    effectivedate: field('terms', 'effectiveDate'),
    effectivedateformatted: field('terms', 'effectiveDate'),
    effectivedateusa: field('terms', 'effectiveDate'),
    expirationdate: field('terms', 'leasehold.expirationDate'),
    expirationdateformatted: field('terms', 'leasehold.expirationDate'),
    expirationdateusa: field('terms', 'leasehold.expirationDate'),
    primaryterm: field('terms', 'leasehold.primaryTerm'),
    primarytermwords: field('terms', 'leasehold.primaryTerm'),
    primarytermunits: field('terms', 'leasehold.primaryTermUnits'),
    primarytermplusoption: field('terms', 'leasehold.optionTerm'),
    primarytermplusoptionwords: field('terms', 'leasehold.optionTerm'),
    royalty: field('terms', 'leasehold.royalty'),
    royaltyfraction: field('terms', 'leasehold.royalty'),
    royaltywords: field('terms', 'leasehold.royalty'),
    royaltypercent: field('terms', 'leasehold.royalty'),
    royaltypercentwords: field('terms', 'leasehold.royalty'),
    bonusamount: field('terms', 'bonusAmount'),
    bonusunit: field('terms', 'bonusUnits'),
    totalbonus: field('terms', 'totalBonus'),
    totalbonuscheck: field('terms', 'totalBonus'),
    bankingdays: field('terms', 'bankingDays'),
    bankingdayswords: field('terms', 'bankingDays'),
    bankingdaysunit: field('terms', 'bankingDaysUnit'),
    bankingdaysunitproper: field('terms', 'bankingDaysUnit'),
    optionlanguage: field('terms', 'leasehold.renewalOption'),
    optionbonusamount: field('terms', 'leasehold.optionBonusAmount'),
    optionbonusamountwords: field('terms', 'leasehold.optionBonusAmount'),
    optionamount: field('terms', 'leasehold.optionBonusAmount'),
    optiontotal: field('terms', 'leasehold.optionTotalBonus'),
    optionbonusunit: field('terms', 'leasehold.optionBonusAmountUnits'),
    optionterm: field('terms', 'leasehold.optionTerm'),
    optiontermwords: field('terms', 'leasehold.optionTerm'),
    optionlength: field('terms', 'leasehold.optionTerm'),
    totallength: field('terms', 'leasehold.optionTerm'),
    optiontermunit: field('terms', 'leasehold.optionTermUnits'),
    secondoptionbonusamount: field('terms', 'addOptionBonusAmount'),
    secondoptionbonusamountwords: field('terms', 'addOptionBonusAmount'),
    primaryrentaltotal: field('terms', 'rentalTotalRent'),
    primaryrentaltotalcheck: field('terms', 'rentalTotalRent'),

    leasedby: field('general', 'leasedBy'),
    leasenumber: from('general', 'the lease number assigned on save'),
    dlcnumber: from('general', 'the lease number assigned on save'),
    dlcleasenumber: from('general', 'the lease number assigned on save'),

    lesseename: field('lessee', 'lesseeName'),
    lesseeaddressline1: field('lessee', 'lesseeAddressLine1'),
    lesseeaddressline2: field('lessee', 'lesseeAddressLine2'),
    lesseecity: field('lessee', 'lesseeCity'),
    lesseestate: field('lessee', 'lesseeState'),
    lesseezipcode: field('lessee', 'lesseeZipCode'),

    ownersondocument: field('owner', 'ownersOnDocument'),
    ownersondocumentallcaps: field('owner', 'ownersOnDocument'),
    ownersaddressondocument: field('owner', 'ownersAddressOnDocument'),
    lessorname: from('owner', OWNER_RECORD),
    lessorspouse: from('owner', OWNER_RECORD),
    lessortaxid: from('owner', OWNER_RECORD),
    owneraddressonfile: from('owner', OWNER_RECORD),
    ownercityonfile: from('owner', OWNER_RECORD),
    ownerstateonfile: from('owner', OWNER_RECORD),
    ownerziponfile: from('owner', OWNER_RECORD),
    leasebuyer: from('owner', 'the lease buyer on the ownership'),

    legaldescription: field('tracts', 'rteLegalDescription'),
    rtelegaldescription: field('tracts', 'rteLegalDescription'),
    inlinertelegal: field('tracts', 'rteLegalDescription'),
    shortlegaldescription: field('tracts', 'shortLegalDescription'),
    grossacres: from('tracts', TRACT_LIST),
    originalgross: from('tracts', TRACT_LIST),
    originalgrosssevendigits: from('tracts', TRACT_LIST),
    originalgrossfourdigits: from('tracts', TRACT_LIST),
    netacres: from('tracts', TRACT_LIST),
    netacresfourdigits: from('tracts', TRACT_LIST),
    township: from('tracts', 'the first tract on the list'),
    townshipallcaps: from('tracts', 'the first tract on the list'),
    range: from('tracts', 'the first tract on the list'),
    sectionnumber: from('tracts', 'the first tract on the list'),
    tractname: from('tracts', 'the first tract on the list'),
    county: from('tracts', 'the first tract on the list'),
    countyallcaps: from('tracts', 'the first tract on the list'),
    easterntownshipnumber: from('tracts', 'the first tract on the list'),
    easterntownshipnumberallcaps: from('tracts', 'the first tract on the list'),
    tractsourcedeed: from('tracts', `the first tract's horizon`),

    clauses: from('addendum', 'the selected client approved and ad hoc clauses'),
    acknowledgmentrte: from('acknowledgment', 'the acknowledgments on this lease'),

    state: from('general', PROJECT_SETTINGS),
    stateallcaps: from('general', PROJECT_SETTINGS),
    numberofdraftdays: from('general', PROJECT_SETTINGS),
    bankingorcalendar: from('general', PROJECT_SETTINGS),
    projectname: from('general', PROJECT_SETTINGS),
    projectid: from('general', PROJECT_SETTINGS),
    clientid: from('general', PROJECT_SETTINGS),
};

const SIGNATURE_TOKEN = /^signature(name|title)\d+$/;

export function leasePreviewTarget(token: string): ILeasePreviewTarget {
    const key = (token || '').toLowerCase();
    if (SIGNATURE_TOKEN.test(key)) {
        return from('acknowledgment', 'the signature blocks on this lease');
    }
    return TARGETS[key];
}



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/livePreview/leasePreviewTargets.ts